

import React, { useEffect, useState } from 'react';
import { Lock, AlertCircle, CheckCircle2, Save, FileText, XCircle, Info, ChevronDown, ChevronUp, GraduationCap } from 'lucide-react';

const InputPAK = ({ onSwitchTab }) => {
  const [status, setStatus] = useState({ loading: true, canInput: false, missingDocs: [], isSubmitted: false, submissionStatus: '' });
  const BASE_URL = process.env.REACT_APP_API_URL;
  const [profile, setProfile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [hasSkp2026, setHasSkp2026] = useState(false);
const [triwulan2026, setTriwulan2026] = useState(4); // Default 4 (Setahun penuh)
const [predikat2026, setPredikat2026] = useState("Baik");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const [formData, setFormData] = useState({
    akLama: '',
    skp1_kode: '', skp1_sudah_pak: false,
    skp2_kode: '', skp2_sudah_pak: false,
    skp3_kode: '', skp3_sudah_pak: false,
  });

  useEffect(() => { fetchInitialData(); }, []);

  const fetchInitialData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      const [resDocs, resStatus, resProfile] = await Promise.all([
        fetch(`${BASE_URL}/api/check-upload-completeness`, { headers }),
        fetch(`${BASE_URL}/api/pak/status`, { headers }),
        fetch(`${BASE_URL}/api/profile`, { headers })
      ]);

      if (!resDocs.ok || !resStatus.ok || !resProfile.ok) throw new Error("Server Error");

      const docsData = await resDocs.json();
      const statusData = await resStatus.json();
      const profileData = await resProfile.json();
      
      setProfile(profileData);
      
      // Jika sudah pernah submit, tampilkan data yang diinput ke dalam form
      if (statusData.submitted) {
        const skp = typeof statusData.skp_data === 'string' ? JSON.parse(statusData.skp_data) : (statusData.skp_data || []);
        setFormData({
            akLama: statusData.ak_lama || '',
            skp1_kode: skp.find(s => s.tahun === 2023)?.kode || '',
            skp1_sudah_pak: skp.find(s => s.tahun === 2023)?.sudah_pak || false,
            skp2_kode: skp.find(s => s.tahun === 2024)?.kode || '',
            skp2_sudah_pak: skp.find(s => s.tahun === 2024)?.sudah_pak || false,
            skp3_kode: skp.find(s => s.tahun === 2025)?.kode || '',
            skp3_sudah_pak: skp.find(s => s.tahun === 2025)?.sudah_pak || false,
        });
      }

      setStatus({
        loading: false, 
        canInput: docsData.canInput, 
        missingDocs: docsData.missingDocs || [],
        isSubmitted: statusData.submitted && statusData.status !== 'Ditolak', 
        submissionStatus: statusData.status || ''
      });
    } catch (err) {
      setStatus(prev => ({ ...prev, loading: false, canInput: false, missingDocs: ['Error server.'] }));
    }
  };

  const isIntegrasi = (profile?.jenis_pak || 'Integrasi').toLowerCase() === 'integrasi';

  const handlePreSubmit = (e) => { 
    e.preventDefault(); 
    
    // Cegah submit jika status terkunci
    if (status.isSubmitted) return;

    if (isIntegrasi) {
        if (!formData.skp1_kode || !formData.skp2_kode || !formData.skp3_kode) {
            showToast("Untuk PAK Integrasi, Nilai SKP 2023, 2024, dan 2025 WAJIB diisi penuh!", "error");
            return; 
        }
    }
    
    setShowConfirmModal(true); 
  };

  const executeSubmit = async () => {
    setShowConfirmModal(false); setIsSaving(true);
    try {
      const token = localStorage.getItem('token');
      const skpEntries = [
        { tahun: 2023, kode: formData.skp1_kode, sudah_pak: formData.skp1_sudah_pak },
        { tahun: 2024, kode: formData.skp2_kode, sudah_pak: formData.skp2_sudah_pak },
        { tahun: 2025, kode: formData.skp3_kode, sudah_pak: formData.skp3_sudah_pak },
      ].filter(skp => skp.kode !== '');

      const payload = {
        jenisPak: profile?.jenis_pak || 'Integrasi',
        akLama: formData.akLama,
        tahunPG: profile?.is_pencantuman_gelar === 'Ya' ? 'Ya' : '', 
        akPG: 0, 
        skpData: skpEntries,
        has_skp_2026: hasSkp2026,
        triwulan_2026: hasSkp2026 ? triwulan2026 : null,
        predikat_2026: hasSkp2026 ? predikat2026 : null
      };

      const res = await fetch(`${BASE_URL}/api/pak`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify(payload)
      });

      if (res.ok) { showToast("Usulan PAK berhasil dikirim!", "success"); fetchInitialData(); } 
      else { showToast("Gagal mengirim data. Silakan coba lagi.", "error"); }
    } catch (err) { showToast("Terjadi kesalahan sistem/jaringan.", "error"); } 
    finally { setIsSaving(false); }
  };

  const handleInputChange = (e) => {
      if (status.isSubmitted) return; // Cegah ketikan jika mode read-only
      const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
      setFormData({ ...formData, [e.target.name]: value });
  };

  if (status.loading) return <div className="p-8 text-center text-slate-500">Memuat data...</div>;
  if (!status.canInput) return <div className="p-10 text-center"><Lock className="mx-auto text-red-500 mb-4" size={48}/> Lengkapi dokumen dulu.</div>;

  return (
    <div className="max-w-5xl mx-auto p-6 md:p-8 relative">
      
      {toast.show && (
        <div className={`fixed top-6 right-6 z-[9999] flex items-center gap-3 px-6 py-4 rounded-xl shadow-2xl text-white animate-fade-in-up ${toast.type === 'success' ? 'bg-emerald-600' : 'bg-rose-600'}`}>
          {toast.type === 'success' ? <CheckCircle2 size={24} /> : <XCircle size={24} />}
          <p className="font-semibold">{toast.message}</p>
        </div>
      )}

      {showConfirmModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/80 backdrop-blur-sm px-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-fade-in-up">
            <div className="bg-blue-50 p-5 flex items-center gap-4 border-b border-blue-100">
              <div className="bg-blue-100 p-2.5 rounded-full text-blue-600"><AlertCircle size={24} /></div>
              <h3 className="font-bold text-lg text-blue-900">Konfirmasi Pengiriman</h3>
            </div>
            <div className="p-6">
              <p className="text-slate-600 mb-6 text-sm leading-relaxed">
                Apakah Anda yakin ingin mengirim usulan PAK ini? Pastikan <strong>Nilai PAK Terakhir</strong> dan <strong>Riwayat SKP</strong> Anda sudah terisi dengan benar.
              </p>
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setShowConfirmModal(false)} className="px-5 py-2.5 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-xl font-medium transition-all">Batal</button>
                <button type="button" onClick={executeSubmit} className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold shadow-lg transition-all flex items-center gap-2">
                  Ya, Kirim Usulan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6 border-b pb-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">Formulir Input PAK</h1>
        <div className="flex gap-2">
            <div className={`px-4 py-1.5 rounded-lg border font-bold text-xs flex items-center gap-2 shadow-sm ${isIntegrasi ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-purple-50 text-purple-700 border-purple-200'}`}>
              MODE: {isIntegrasi ? 'INTEGRASI' : 'KONVERSI'}
            </div>
        </div>
      </div>

      {status.isSubmitted && (
        <div className="mb-6 bg-blue-50 border border-blue-200 p-4 rounded-xl shadow-sm flex items-start gap-3 animate-fade-in-up">
          <Lock className="text-blue-600 shrink-0 mt-0.5" size={24} />
          <div>
            <h3 className="font-bold text-blue-900 text-base">Data PAK Terkunci (Mode Read-Only)</h3>
            <p className="text-sm text-blue-800 mt-1">Usulan Bapak/Ibu sedang dalam proses verifikasi oleh Admin/Satlak sehingga data di bawah ini tidak dapat diubah kembali.</p>
          </div>
        </div>
      )}

      <div className="mb-6 bg-white border border-blue-200 rounded-2xl shadow-sm overflow-hidden">
        <button type="button" onClick={() => setShowGuide(!showGuide)} className="w-full flex items-center justify-between p-4 md:p-5 bg-blue-50 hover:bg-blue-100 transition-colors">
          <div className="flex items-center gap-3 text-left">
            <Info className="text-blue-600 shrink-0" size={24} />
            <h3 className="font-bold text-blue-900 text-base md:text-lg">Panduan Singkat: Bagaimana Sistem Menghitung Angka Kredit Bapak/Ibu?</h3>
          </div>
          {showGuide ? <ChevronUp className="text-blue-600 shrink-0" size={20} /> : <ChevronDown className="text-blue-600 shrink-0" size={20} />}
        </button>
        {showGuide && (
          <div className="p-5 md:p-6 border-t border-blue-100 text-sm text-slate-700 leading-relaxed animate-fade-in-up">
            <p className="mb-4">Agar Bapak/Ibu memiliki gambaran terkait target Angka Kredit (AK) yang dibutuhkan untuk naik pangkat/jabatan beserta perhitungannya, silakan simak panduan di bawah ini:</p>
            <div className="space-y-6">
              <div className="bg-slate-50 p-4 md:p-5 rounded-xl border border-slate-200 shadow-sm">
                <p className="font-bold text-blue-800 text-sm uppercase mb-3 tracking-wider border-b border-slate-200 pb-2">1. Target Nilai per Golongan</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="bg-white p-3 rounded-lg border border-slate-200">
                    <p className="font-bold text-slate-800">Guru Ahli Pertama (Gol. III/a - III/b)</p>
                    <ul className="mt-1 space-y-1 text-slate-600"><li>• Nilai Dasar Kinerja: <strong className="text-blue-700">12,5</strong> / tahun</li><li>• Syarat Naik Pangkat: Butuh <strong className="text-emerald-600">50 AK</strong></li></ul>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-slate-200">
                    <p className="font-bold text-slate-800">Guru Ahli Muda (Gol. III/c - III/d)</p>
                    <ul className="mt-1 space-y-1 text-slate-600"><li>• Nilai Dasar Kinerja: <strong className="text-blue-700">25</strong> / tahun</li><li>• Syarat Naik Pangkat: Butuh <strong className="text-emerald-600">100 AK</strong></li></ul>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-slate-200">
                    <p className="font-bold text-slate-800">Guru Ahli Madya (Gol. IV/a - IV/c)</p>
                    <ul className="mt-1 space-y-1 text-slate-600"><li>• Nilai Dasar Kinerja: <strong className="text-blue-700">37,5</strong> / tahun</li><li>• Syarat Naik Pangkat: Butuh <strong className="text-emerald-600">150 AK</strong></li></ul>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-slate-200">
                    <p className="font-bold text-slate-800">Guru Ahli Utama (Gol. IV/d)</p>
                    <ul className="mt-1 space-y-1 text-slate-600"><li>• Nilai Dasar Kinerja: <strong className="text-blue-700">50</strong> / tahun</li><li>• Syarat Naik Pangkat: Butuh <strong className="text-emerald-600">200 AK</strong></li></ul>
                  </div>
                </div>
              </div>
              <div>
                <p className="font-bold text-slate-800 text-base mb-1">2. Perhitungan Nilai Kinerja (SKP) Tahunan</p>
                <p className="mb-2">Nilai yang Bapak/Ibu dapatkan setiap tahunnya dipengaruhi oleh Predikat SKP (Jika "Baik" bernilai 100%, jika "Amat Baik" bernilai 150%).</p>
                <div className="bg-amber-50 p-4 rounded-xl border border-amber-200 text-amber-900">
                  <p className="font-bold text-amber-900 mb-1 flex items-center gap-2"><FileText size={16}/> Contoh Kasus SKP:</p>
                  <p>Jika Bapak/Ibu adalah Guru Golongan <b>III/a</b>. Sesuai tabel di atas, Nilai Dasar tahunan Bapak/Ibu adalah <b>12,5</b>. Jika pada tahun tersebut (selama 12 bulan penuh) Bapak/Ibu mendapat predikat SKP:</p>
                  <ul className="list-disc ml-5 mt-2 space-y-1">
                    <li><b>Baik (100%)</b>, maka sistem menghitung: 100% × 12,5 = <strong className="text-emerald-700">Mendapat 12,5 AK</strong>.</li>
                    <li><b>Amat Baik (150%)</b>, maka sistem menghitung: 150% × 12,5 = <strong className="text-emerald-700">Mendapat 18,75 AK</strong>.</li>
                  </ul>
                </div>
              </div>
              <div>
                <p className="font-bold text-slate-800 text-base mb-1">3. Tambahan Nilai Peningkatan Pendidikan (Gelar Baru)</p>
                <p className="mb-2">Jika Bapak/Ibu mencantumkan Ijazah/Gelar Baru, sistem otomatis memberikan bonus sebesar <b>25% dari Kebutuhan Angka Kredit Naik Pangkat</b> Bapak/Ibu.</p>
                <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-200 text-emerald-900">
                  <p className="font-bold text-emerald-900 mb-1 flex items-center gap-2"><GraduationCap size={16}/> Contoh Kasus Gelar Baru:</p>
                  <p>Jika Bapak/Ibu adalah Guru Golongan <b>III/b</b>. Syarat bagi Bapak/Ibu untuk naik pangkat ke III/c adalah butuh <b>50 AK</b>.</p>
                  <p className="mt-1">Jika Bapak/Ibu baru saja lulus S2 dan mengusulkan gelarnya, maka bonus yang didapatkan dari sistem adalah: <br/> <b>25% × 50 AK (Syarat naik pangkat)</b> = <strong className="text-emerald-700">Tambahan sebesar 12,5 AK</strong>.</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <form onSubmit={handlePreSubmit} className="bg-white p-6 md:p-8 rounded-2xl border shadow-sm space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-b pb-6">
          <div>
            <label className="block text-sm font-bold mb-1">Nilai PAK Terakhir (Total)</label>
            <input required type="number" step="0.001" name="akLama" value={formData.akLama} 
                onChange={handleInputChange} disabled={status.isSubmitted}
                className={`w-full border p-2.5 rounded-lg outline-none transition-colors ${status.isSubmitted ? 'bg-slate-100 cursor-not-allowed text-slate-500' : 'focus:border-blue-500'}`} placeholder="102.000" />
          </div>
        </div>

        <div className="pt-2">
          <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><FileText size={18} /> Riwayat Penilaian Kinerja (SKP)</h3>
          
          <div className="mb-5 bg-blue-50 border border-blue-100 p-4 rounded-xl shadow-sm">
            <p className="text-sm text-blue-800 leading-relaxed">
              <strong className="text-blue-900 block mb-1">Penting Saat Mengisi SKP:</strong>
              1. <b>Tahun Penilaian SKP telah dikunci di sistem (2023, 2024, 2025).</b> Silakan isi predikat kinerja (SKP) Bapak/Ibu pada tahun yang bersangkutan.<br/>
              2. Jika pada tahun tertentu Bapak/Ibu <b>sudah memiliki cetakan PAK Konversi dari Sudin/Dinas</b>, mohon <b>centang kotaknya</b>. Jika dicentang, SKP tahun tersebut tidak akan dicetak ulang oleh sistem.
            </p>
          </div>
          
          <div className="space-y-4">
             {[1, 2, 3].map((num) => {
              const currentYearInt = 2022 + num;
              const currentYearStr = String(currentYearInt);

              return (
                <div key={num} className={`p-4 rounded-lg border flex flex-col gap-3 transition-all ${status.isSubmitted ? 'bg-slate-50 border-slate-200' : 'bg-white hover:border-blue-300'}`}>
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="w-full md:w-24">
                      <label className="text-xs font-bold text-slate-500 block mb-1">Tahun</label>
                      <input type="number" value={currentYearStr} readOnly className="w-full border p-2 rounded font-bold cursor-not-allowed bg-slate-200 text-slate-600" />
                    </div>
                    <div className="flex-1">
                      <label className="text-xs font-bold text-slate-500 block mb-1">Nilai SKP</label>
                      <select name={`skp${num}_kode`} value={formData[`skp${num}_kode`]} onChange={handleInputChange} disabled={status.isSubmitted} 
                        className={`w-full border p-2 rounded outline-none transition-colors ${status.isSubmitted ? 'bg-slate-100 cursor-not-allowed text-slate-500' : 'bg-white focus:ring-2 focus:ring-blue-500'}`}>
                        <option value="">-- Kosong / Tidak Ada Nilai --</option>
                        <option value="1">Amat Baik</option>
                        <option value="2">Baik</option>
                        <option value="3">Cukup / Kurang Baik</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="ml-0 md:ml-28 flex items-center gap-2 mt-1 relative z-10">
                     <input 
                        type="checkbox" 
                        id={`skp${num}_sudah_pak`} 
                        name={`skp${num}_sudah_pak`} 
                        checked={formData[`skp${num}_sudah_pak`]} 
                        onChange={handleInputChange} 
                        disabled={status.isSubmitted}
                        className={`w-4 h-4 ${status.isSubmitted ? 'cursor-not-allowed text-slate-400' : 'cursor-pointer text-blue-600'}`}
                     />
                     <label htmlFor={`skp${num}_sudah_pak`} className={`text-xs font-medium select-none ${status.isSubmitted ? 'cursor-not-allowed text-slate-400' : 'cursor-pointer text-slate-700'}`}>
                       Apakah Saudara Punya <strong>Konversi Predikat Kinerja</strong> di tahun ini? <br/>
                       <span className="text-[10px] font-normal">(Centang jika YA. PAK Konversi Predikat Kinerja tahun ini tidak akan dicetak di file PDF)</span>
                     </label>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
{/* --- TAMBAHAN BARU: Checkbox SKP 2026 --- */}
        <div className="mt-6 p-5 border border-blue-200 rounded-xl bg-blue-50/50 shadow-sm transition-all">
            <label className={`flex items-center space-x-3 select-none ${status.isSubmitted ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                <input 
                    type="checkbox" 
                    checked={hasSkp2026}
                    onChange={(e) => setHasSkp2026(e.target.checked)}
                    disabled={status.isSubmitted}
                    className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <span className={`font-bold text-base ${status.isSubmitted ? 'text-slate-500' : 'text-blue-900'}`}>
                    Tambahkan SKP 2026 (Opsional)
                </span>
            </label>

            {hasSkp2026 && (
                <div className="mt-5 ml-8 grid grid-cols-1 md:grid-cols-2 gap-5 animate-fade-in-up">
                    <div>
                        <label className="block text-xs font-bold text-slate-500 mb-1.5">Periode Triwulan (TW)</label>
                        <select 
                            value={triwulan2026} 
                            onChange={(e) => setTriwulan2026(parseInt(e.target.value))}
                            disabled={status.isSubmitted}
                            className={`w-full border p-2.5 rounded-lg outline-none transition-colors ${status.isSubmitted ? 'bg-slate-100 cursor-not-allowed text-slate-500' : 'bg-white focus:ring-2 focus:ring-blue-500'}`}
                        >
                            <option value={3}>TW 1 (Jan - Mar)</option>
                            <option value={6}>TW 2 (Jan - Jun)</option>
                            <option value={9}>TW 3 (Jan - Sep)</option>
                            <option value={12}>TW 4 (Full 1 Tahun)</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 mb-1.5">Predikat SKP 2026</label>
                        <select 
                            value={predikat2026} 
                            onChange={(e) => setPredikat2026(e.target.value)}
                            disabled={status.isSubmitted}
                            className={`w-full border p-2.5 rounded-lg outline-none transition-colors ${status.isSubmitted ? 'bg-slate-100 cursor-not-allowed text-slate-500' : 'bg-white focus:ring-2 focus:ring-blue-500'}`}
                        >
                            <option value="1">Amat Baik</option>
                            <option value="2">Baik</option>
                            <option value="3">Cukup / Kurang Baik</option>
                        </select>
                    </div>
                </div>
            )}
        </div>
        {/* --- AKHIR TAMBAHAN BARU --- */}
        <div className="pt-8 flex justify-end border-t border-slate-100">
          {/* TOMBOL FULL WIDTH DAN BESAR */}
          <button type="submit" disabled={isSaving || status.isSubmitted} 
            className={`w-full py-4 text-base md:text-lg rounded-xl font-bold shadow-lg flex items-center justify-center gap-2 transition-all ${isSaving || status.isSubmitted ? 'bg-slate-400 cursor-not-allowed text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}>
            <Save size={24}/> {isSaving ? 'Menyimpan Data...' : status.isSubmitted ? 'USULAN SEDANG DALAM PROSES VERIFIKASI' : 'KIRIM DAN SIMPAN USULAN PAK'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default InputPAK;